import { Injectable, InjectionToken, Inject, inject } from '@angular/core';
import * as moment from 'moment-timezone';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
  HttpHeaderResponse,
  HttpProgressEvent,
  HttpResponse,
  HttpUserEvent,
  HttpSentEvent,
} from '@angular/common/http';
import { TokenService } from '../../services/token/token.service';
import { AuthService } from '../../services/auth/auth.service';
import { SharedService } from '../../services/shared/shared.service';
export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {
  private readonly _timezone = moment.tz.guess();
  private _isRefreshingToken = false;
  private _refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _authService = inject(AuthService);
  private _tokenService = inject(TokenService);
  private _sharedService = inject(SharedService);

  constructor(@Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number) {}

  addAuthTokenToRequest(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.headers.has('Authorization')
      ? req
      : req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<
    HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>
  > {
    const requestUrl = request.url.toLowerCase();
    const skip = ['/record', '/i18n/en.json', '-staff'];
    const skipAuth = ['overdrivepatronlogin', 'forgotpassword', 'updatepw'];

    const tokenFactory = {
      setToken: (newToken) => {
        this._tokenService.setToken(newToken);
      },

      getToken: () => {
        return this._tokenService.getToken();
      },
    };

    if (!request.headers.has('X-Timezone')) {
      request = request.clone({ headers: request.headers.set('X-Timezone', this._timezone) });
    }

    if (
      (request.method === 'POST' &&
        (requestUrl.includes('/auth/') ||
          requestUrl.includes('/auth?') ||
          requestUrl.endsWith('/auth')) &&
        !skipAuth.some((s) => requestUrl.includes(s))) ||
      skip.some((s) => requestUrl.includes(s))
    ) {
      return next.handle(request);
    } else if (
      requestUrl.includes('lendingpolicy?agent=') ||
      requestUrl.includes('ws/ill') ||
      (request.method === 'GET' && requestUrl.includes('/auth?agent='))
    ) {
      tokenFactory.setToken = (newToken) => {
        this._tokenService.setResourceSharingToken(newToken);
      };
      tokenFactory.getToken = () => {
        return this._tokenService.getResourceSharingToken();
      };
    }
    return this._handleTokenizedRequest(tokenFactory, request, next);
  }

  private _handleTokenizedRequest(tokenFactory: any, request: HttpRequest<any>, next: HttpHandler) {
    const token = tokenFactory.getToken();
    if (token) {
      request = this.addAuthTokenToRequest(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (
          token &&
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          (!(request.method.toUpperCase() == 'GET' && request.url.includes('/auth')) ||
            request.url.includes('/auth/logout'))
        ) {
          return this._handle401Error(tokenFactory, request, next, error);
        }
        return throwError(error);
      }),
    );
  }

  private _handle401Error(
    tokenFactory: any,
    req: HttpRequest<any>,
    next: HttpHandler,
    previousError: any,
  ) {
    if (!this._isRefreshingToken) {
      this._isRefreshingToken = true;
      this._refreshTokenSubject.next(null);

      return this._authService.refreshToken(tokenFactory.getToken()).pipe(
        finalize(() => {
          this._isRefreshingToken = false;
        }),
        switchMap((newToken) => {
          this._refreshTokenSubject.next(newToken);
          tokenFactory.setToken(newToken);
          return next.handle(
            this.addAuthTokenToRequest(
              req.clone({ headers: req.headers.delete('Authorization') }),
              newToken,
            ),
          );
        }),
        catchError((error: unknown) => {
          if (!req.url.includes('/auth/logout')) {
            console.error(
              `Refresh token failed, request: ${req}, token: ${this._tokenService.getToken()}`,
            );
            this._sharedService.openSnackBar('Session expired, please logout', 'ok', 10000);
          }
          return throwError(error);
        }),
      );
    } else {
      return this._refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return jwt
            ? next.handle(this.addAuthTokenToRequest(req, jwt))
            : throwError(previousError);
        }),
      );
    }
  }
}
